import { useReducer, useCallback } from 'react';
import type { RecurringFrequency } from '@/lib/domains';

/**
 * Form data shape for add expense form
 */
export interface AddExpenseFormData {
    amount: string;
    currency: string;
    date: string;
    categoryId: string;
    description: string;
    frequency: RecurringFrequency;
    endDate: string;
}

/**
 * Complete form state including metadata
 */
export interface AddExpenseFormState {
    formData: AddExpenseFormData;
    isRecurring: boolean;
    isSubmitting: boolean;
    errors: {
        amount?: string;
        category?: string;
    };
}

/**
 * Discriminated union of all possible form actions
 */
type FormAction =
    | { type: 'SET_FIELD'; field: keyof AddExpenseFormData; value: string }
    | { type: 'SET_MULTIPLE_FIELDS'; updates: Partial<AddExpenseFormData> }
    | { type: 'SET_RECURRING'; isRecurring: boolean }
    | { type: 'SET_SUBMITTING'; isSubmitting: boolean }
    | { type: 'SET_ERROR'; field: 'amount' | 'category'; message: string }
    | { type: 'CLEAR_ERRORS' }
    | { type: 'CLEAR_ERROR'; field: 'amount' | 'category' }
    | { type: 'RESET_FORM'; defaultCurrency: string; defaultCategoryId: string };

/**
 * Form reducer for add expense page
 * Manages complex form state with validation errors and metadata
 */
function formReducer(state: AddExpenseFormState, action: FormAction): AddExpenseFormState {
    switch (action.type) {
        case 'SET_FIELD':
            return {
                ...state,
                formData: {
                    ...state.formData,
                    [action.field]: action.value,
                },
            };

        case 'SET_MULTIPLE_FIELDS':
            return {
                ...state,
                formData: {
                    ...state.formData,
                    ...action.updates,
                },
            };

        case 'SET_RECURRING':
            return {
                ...state,
                isRecurring: action.isRecurring,
            };

        case 'SET_SUBMITTING':
            return {
                ...state,
                isSubmitting: action.isSubmitting,
            };

        case 'SET_ERROR':
            return {
                ...state,
                errors: {
                    ...state.errors,
                    [action.field]: action.message,
                },
            };

        case 'CLEAR_ERRORS':
            return {
                ...state,
                errors: {},
            };

        case 'CLEAR_ERROR':
            const newErrors = { ...state.errors };
            delete newErrors[action.field];
            return {
                ...state,
                errors: newErrors,
            };

        case 'RESET_FORM':
            return {
                ...state,
                formData: {
                    amount: '',
                    currency: action.defaultCurrency,
                    date: new Date().toISOString().split('T')[0],
                    categoryId: action.defaultCategoryId,
                    description: '',
                    frequency: 'monthly',
                    endDate: '',
                },
                isRecurring: false,
                isSubmitting: false,
                errors: {},
            };

        default:
            return state;
    }
}

/**
 * Custom hook for add expense form state management
 * 
 * Replaces 8+ useState calls with a single useReducer for better maintainability.
 * Provides type-safe actions and centralized state transitions.
 * 
 * @param defaultCurrency - User's preferred currency
 * @param defaultCategoryId - Initial category selection
 * @returns Form state and action dispatchers
 * 
 * @example
 * ```tsx
 * const { state, actions } = useAddExpenseForm('USD', 'cat-123');
 * 
 * // Update single field
 * actions.setField('amount', '50.00');
 * 
 * // Update multiple fields (e.g., from quick add)
 * actions.setMultipleFields({
 *   amount: '50.00',
 *   categoryId: 'cat-456',
 *   description: 'Coffee'
 * });
 * 
 * // Handle errors
 * actions.setError('amount', 'Amount is required');
 * actions.clearError('amount');
 * ```
 */
export function useAddExpenseForm(defaultCurrency: string, defaultCategoryId: string) {
    const initialState: AddExpenseFormState = {
        formData: {
            amount: '',
            currency: defaultCurrency,
            date: new Date().toISOString().split('T')[0],
            categoryId: defaultCategoryId,
            description: '',
            frequency: 'monthly',
            endDate: '',
        },
        isRecurring: false,
        isSubmitting: false,
        errors: {},
    };

    const [state, dispatch] = useReducer(formReducer, initialState);

    // Memoized action creators for stable references
    const actions = {
        setField: useCallback((field: keyof AddExpenseFormData, value: string) => {
            dispatch({ type: 'SET_FIELD', field, value });
        }, []),

        setMultipleFields: useCallback((updates: Partial<AddExpenseFormData>) => {
            dispatch({ type: 'SET_MULTIPLE_FIELDS', updates });
        }, []),

        setRecurring: useCallback((isRecurring: boolean) => {
            dispatch({ type: 'SET_RECURRING', isRecurring });
        }, []),

        setSubmitting: useCallback((isSubmitting: boolean) => {
            dispatch({ type: 'SET_SUBMITTING', isSubmitting });
        }, []),

        setError: useCallback((field: 'amount' | 'category', message: string) => {
            dispatch({ type: 'SET_ERROR', field, message });
        }, []),

        clearErrors: useCallback(() => {
            dispatch({ type: 'CLEAR_ERRORS' });
        }, []),

        clearError: useCallback((field: 'amount' | 'category') => {
            dispatch({ type: 'CLEAR_ERROR', field });
        }, []),

        resetForm: useCallback((defaultCurrency: string, defaultCategoryId: string) => {
            dispatch({ type: 'RESET_FORM', defaultCurrency, defaultCategoryId });
        }, []),
    };

    return { state, actions };
}
